import { useState, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@services/firebase';

import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  Send as SendIcon,
  Email as EmailIcon,
  CheckCircle,
} from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';

const SupportPage = () => {
  const { currentUser, userData } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: userData?.displayName || '',
    email: currentUser?.email || '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Pre-fill form once user data loads (userData arrives async from Firestore)
  useEffect(() => {
    if (userData || currentUser) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || userData?.displayName || '',
        email: prev.email || currentUser?.email || '',
      }));
    }
  }, [userData, currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (!formData.subject.trim() || !formData.message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!formData.email.trim()) {
      setError('Please provide your email address.');
      return;
    }

    try {
      setLoading(true);

      // reCAPTCHA v3 verification
      if (executeRecaptcha) {
        const token = await executeRecaptcha('contact_support');
        const verifyRecaptcha = httpsCallable(functions, 'verifyRecaptcha');
        const result = await verifyRecaptcha({ token });
        if (!result.data.success) {
          setError('Bot check failed. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Submit support request via Cloud Function (handles admin notification + confirmation email)
      const sendSupportRequest = httpsCallable(functions, 'sendSupportRequest');
      await sendSupportRequest({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });

      // Reset form
      setFormData({
        name: userData?.displayName || '',
        email: currentUser?.email || '',
        subject: '',
        message: '',
      });

      setSuccess(true);
    } catch (err) {
      console.error('Error submitting support request:', err);
      setError('Failed to send your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
          Contact Support
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Need help? Send us a message and our team will get back to you soon.
        </Typography>
      </Box>

      {/* Success Message */}
      {success && (
        <Alert
          severity="success"
          icon={<CheckCircle />}
          sx={{ mb: 3 }}
          onClose={() => setSuccess(false)}
        >
          <Typography variant="body1" fontWeight="medium">
            Message sent successfully!
          </Typography>
          <Typography variant="body2">
            Our support team has been notified and will respond to you shortly.
          </Typography>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Support Form */}
      <Paper elevation={3} sx={{ p: { xs: 3, md: 4 } }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Name Field */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Your Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={loading || !!currentUser}
                variant="outlined"
                InputLabelProps={currentUser ? { shrink: true } : undefined}
                helperText={currentUser ? 'From your profile' : undefined}
              />
            </Grid>

            {/* Email Field */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading || !!currentUser}
                variant="outlined"
                InputLabelProps={currentUser ? { shrink: true } : undefined}
                helperText={currentUser ? 'From your profile' : "We'll use this to respond to you"}
              />
            </Grid>

            {/* Subject Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                disabled={loading}
                variant="outlined"
                placeholder="Brief description of your issue"
              />
            </Grid>

            {/* Message Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                disabled={loading}
                variant="outlined"
                multiline
                rows={6}
                placeholder="Please provide details about your question or issue..."
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{ minHeight: 48 }}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Additional Help */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          You can also visit{' '}
          <a
            href="https://servesync.me"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            servesync.me
          </a>{' '}
          for more information.
        </Typography>
      </Box>
    </Container>
  );
};

export default SupportPage;
